import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {
  CheckCheck, Globe2,
  Phone, Smile,
  Video, X,
} from 'lucide-react';
import {RootState} from '../../store';
import {
  messagingApi, MessagingAttachmentInput, MessagingConversation, MessagingMessage,
  MessagingParticipant,
} from '../../services/messagingApi';
import {profileApi, ProfileApiUser} from '../../services/profileApi';
import {socialFeedApi} from '../../services/socialFeedApi';
import {useRealtime} from '../../realtime/RealtimeProvider';
import ProfileWarningIcon from './profile/components/ProfileWarningIcon';
import styles from './messages/Messages.module.css';
import './Main.css';

type InboxView = 'all' | 'unread' | 'archived';
const MOBILE_THREAD_MEDIA_QUERY = '(max-width: 767px)';

const isMobileThreadViewport = () => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia(MOBILE_THREAD_MEDIA_QUERY).matches;

const EMOJIS = [
  '😀', '😊', '😂', '😍', '👍', '👏',
  '🎉', '❤️', '🔥', '🙌', '💜', '✨',
  '😁', '😅', '🤣', '🥰', '😎', '🤔',
  '😢', '😭', '😡', '🤯', '🥳', '🤗',
  '👋', '🤝', '🙏', '💪', '👌', '💯',
  '⭐', '🌟', '🎂', '🎁', '🚀', '✅',
];
const messageFillIconStyle = {
  '--message-fill-icon-url': `url("${process.env.PUBLIC_URL}/icons/message-fill.svg")`,
} as React.CSSProperties;
const messageOutlineIconStyle = {
  '--message-outline-icon-url': `url("${process.env.PUBLIC_URL}/icons/message.svg")`,
} as React.CSSProperties;
const conversationOptionsIconStyle = {
  '--conversation-options-icon-url': `url("${process.env.PUBLIC_URL}/icons/dots-three-bold.svg")`,
} as React.CSSProperties;
const paperclipIconStyle = {
  '--paperclip-icon-url': `url("${process.env.PUBLIC_URL}/icons/paperclip.svg")`,
} as React.CSSProperties;
const sendIconStyle = {
  '--send-icon-url': `url("${process.env.PUBLIC_URL}/icons/send.svg")`,
} as React.CSSProperties;
const calendarFilledIconStyle = {
  '--calendar-filled-icon-url': `url("${process.env.PUBLIC_URL}/icons/calendar-filled.svg")`,
} as React.CSSProperties;
const locationLineIconStyle = {
  '--location-line-icon-url': `url("${process.env.PUBLIC_URL}/icons/location-line.svg")`,
} as React.CSSProperties;
const arrowLeftBoldIconStyle = {
  '--arrow-left-bold-icon-url': `url("${process.env.PUBLIC_URL}/icons/arrow-left-bold.svg")`,
} as React.CSSProperties;

const safeDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatTime = (value: string) => safeDate(value)?.toLocaleTimeString([], {
  hour: 'numeric', minute: '2-digit',
}) || '';

const formatConversationDate = (value: string) => {
  const date = safeDate(value);
  if (!date) return '';
  const today = new Date();
  const dayDifference = Math.floor((new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()
    - new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()) / 86400000);
  if (dayDifference === 0) return formatTime(value);
  if (dayDifference < 7) return date.toLocaleDateString([], {weekday: 'short'});
  return date.toLocaleDateString([], {month: 'short', day: 'numeric'});
};

const formatDay = (value: string) => {
  const date = safeDate(value);
  if (!date) return '';
  const today = new Date();
  const dateKey = date.toDateString();
  if (dateKey === today.toDateString()) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (dateKey === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], {month: 'long', day: 'numeric', year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric'});
};

const getInitials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2)
  .map((part) => part.charAt(0).toUpperCase()).join('') || 'U';

const sortConversations = (items: MessagingConversation[]) => [...items].sort(
  (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
);

const dedupeConversationsByParticipants = (items: MessagingConversation[]) => {
  const uniqueConversations = new Map<string, MessagingConversation>();
  sortConversations(items).forEach((conversation) => {
    const participantKey = conversation.participants
      .map((participant) => participant.id)
      .sort()
      .join(':');
    if (!uniqueConversations.has(participantKey)) {
      uniqueConversations.set(participantKey, conversation);
    }
  });
  return [...uniqueConversations.values()];
};

const mergeConversations = (current: MessagingConversation[], incoming: MessagingConversation[]) => {
  const byId = new Map(current.map((item) => [item.id, item]));
  incoming.forEach((item) => byId.set(item.id, item));
  return dedupeConversationsByParticipants([...byId.values()]);
};

const mergeMessages = (current: MessagingMessage[], incoming: MessagingMessage[]) => {
  const byId = new Map(current.map((item) => [item.id, item]));
  incoming.forEach((item) => byId.set(item.id, item));
  return [...byId.values()].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
};

const getOtherParticipant = (conversation: MessagingConversation | null, userId?: string) => (
  conversation?.participants.find((participant) => participant.id !== userId)
  || conversation?.participants[0]
  || null
);

const buildLastMessagePreview = (message: MessagingMessage) => message.content
  ? message.content.slice(0, 100)
  : `Sent ${message.attachments.length === 1 ? 'an attachment' : `${message.attachments.length} attachments`}`;

const presencePresentation = {
  online: {label: 'Online', className: styles.presenceOnline},
  sleep: {label: 'Idle', className: styles.presenceIdle},
  offline: {label: 'Invisible', className: styles.presenceInvisible},
} as const;

const ParticipantAvatar: React.FC<{
  participant: Pick<MessagingParticipant, 'name' | 'profilePicture' | 'presenceStatus'>;
  className?: string;
  showPresence?: boolean;
}> = ({participant, className, showPresence = false}) => {
  const [failed, setFailed] = useState(false);
  const presence = presencePresentation[participant.presenceStatus];
  useEffect(() => setFailed(false), [participant.profilePicture]);
  return (
    <span className={`${styles.avatarWrap} ${className || ''}`.trim()}>
      <span className={styles.participantAvatar}>
        {participant.profilePicture && !failed ? (
          <img src={participant.profilePicture} alt="" onError={() => setFailed(true)} />
        ) : <span>{getInitials(participant.name)}</span>}
      </span>
      {showPresence ? (
        <span
          className={`${styles.presenceDot} ${presence.className}`}
          role="img"
          aria-label={presence.label}
          title={presence.label}
        />
      ) : null}
    </span>
  );
};

const getVideoDurationSeconds = (file: File): Promise<number | undefined> => new Promise((resolve) => {
  if (!file.type.startsWith('video/')) return resolve(undefined);
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.onloadedmetadata = () => {
    resolve(Number.isFinite(video.duration) && video.duration > 0 ? Math.round(video.duration) : undefined);
    URL.revokeObjectURL(url);
  };
  video.onerror = () => { URL.revokeObjectURL(url); resolve(undefined); };
  video.src = url;
});

const Messages: React.FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const location = useLocation();
  const navigate = useNavigate();
  const {reconnectSequence, socket} = useRealtime();
  const [activeConversations, setActiveConversations] = useState<MessagingConversation[]>([]);
  const [archivedConversations, setArchivedConversations] = useState<MessagingConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessagingMessage[]>([]);
  const [inboxView, setInboxView] = useState<InboxView>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [contacts, setContacts] = useState<MessagingParticipant[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [messageLoadError, setMessageLoadError] = useState<string | null>(null);
  const [messageReloadSequence, setMessageReloadSequence] = useState(0);
  const [sendError, setSendError] = useState<string | null>(null);
  const [composerText, setComposerText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [sendLoading, setSendLoading] = useState(false);
  const [profile, setProfile] = useState<ProfileApiUser | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [mobileThreadOpen, setMobileThreadOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [pendingLiveReadId, setPendingLiveReadId] = useState<string | null>(null);
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const moreMenuRef = useRef<HTMLDivElement | null>(null);
  const emojiRef = useRef<HTMLDivElement | null>(null);
  const drawerCloseRef = useRef<HTMLButtonElement | null>(null);
  const previousMessageCountRef = useRef(0);

  const allConversations = inboxView === 'archived' ? archivedConversations : activeConversations;
  const activeConversation = useMemo(() => (
    [...activeConversations, ...archivedConversations].find((item) => item.id === activeConversationId) || null
  ), [activeConversationId, activeConversations, archivedConversations]);
  const otherParticipant = getOtherParticipant(activeConversation, currentUser?.id);
  const profilePanelParticipant = otherParticipant ? {
    ...otherParticipant,
    profilePicture: profile?._id === otherParticipant.id
      ? profile.profile.profilePicture || otherParticipant.profilePicture
      : otherParticipant.profilePicture,
  } : null;
  const filteredConversations = useMemo(() => {
    const normalized = searchQuery.trim().toLowerCase();
    return allConversations.filter((conversation) => {
      if (inboxView === 'unread' && conversation.unreadCount === 0) return false;
      if (!normalized) return true;
      const participant = getOtherParticipant(conversation, currentUser?.id);
      return Boolean(participant?.name.toLowerCase().includes(normalized)
        || conversation.lastMessagePreview.toLowerCase().includes(normalized));
    });
  }, [allConversations, currentUser?.id, inboxView, searchQuery]);

  const loadConversations = React.useCallback(async () => {
    if (!token) return;
    const [active, archived] = await Promise.all([
      messagingApi.getConversations(token, 'active'),
      messagingApi.getConversations(token, 'archived'),
    ]);
    setActiveConversations(dedupeConversationsByParticipants(active));
    setArchivedConversations(dedupeConversationsByParticipants(archived));
  }, [token]);

  useEffect(() => {
    if (!token) { setLoadingConversations(false); return; }
    let mounted = true;
    setLoadingConversations(true);
    loadConversations().catch((error) => mounted && setPageError(error instanceof Error ? error.message : 'Failed to load conversations.'))
      .finally(() => mounted && setLoadingConversations(false));
    return () => { mounted = false; };
  }, [loadConversations, token]);

  useEffect(() => {
    if (activeConversationId) return;
    const firstConversation = inboxView === 'archived'
      ? archivedConversations[0]
      : inboxView === 'unread'
        ? activeConversations.find((item) => item.unreadCount > 0)
        : activeConversations[0];
    if (firstConversation) {
      setActiveConversationId(firstConversation.id);
      if (isMobileThreadViewport()) setMobileThreadOpen(true);
    }
  }, [activeConversationId, activeConversations, archivedConversations, inboxView]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const viewport = window.matchMedia(MOBILE_THREAD_MEDIA_QUERY);
    const handleViewportChange = (event: MediaQueryListEvent) => {
      if (event.matches && activeConversationId) setMobileThreadOpen(true);
    };

    if (typeof viewport.addEventListener === 'function') {
      viewport.addEventListener('change', handleViewportChange);
      return () => viewport.removeEventListener('change', handleViewportChange);
    }

    viewport.addListener(handleViewportChange);
    return () => viewport.removeListener(handleViewportChange);
  }, [activeConversationId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const conversationId = params.get('conversationId')?.trim();
    if (!conversationId) return;
    const match = [...activeConversations, ...archivedConversations].find((item) => item.id === conversationId);
    if (match) {
      setActiveConversationId(match.id);
      setInboxView(archivedConversations.some((item) => item.id === match.id) ? 'archived' : 'all');
      setMobileThreadOpen(true);
      params.delete('conversationId');
      navigate({pathname: location.pathname, search: params.toString() ? `?${params}` : ''}, {replace: true});
    }
  }, [activeConversations, archivedConversations, location.pathname, location.search, navigate]);

  useEffect(() => {
    const recipientId = new URLSearchParams(location.search).get('recipientId')?.trim();
    if (!recipientId || !token) return;
    let mounted = true;
    messagingApi.createConversation(token, recipientId).then((conversation) => {
      if (!mounted) return;
      setActiveConversations((current) => mergeConversations(current, [conversation]));
      setActiveConversationId(conversation.id);
      setMobileThreadOpen(true);
    }).catch((error) => mounted && setPageError(error instanceof Error ? error.message : 'Failed to open conversation.'))
      .finally(() => {
        const params = new URLSearchParams(location.search);
        params.delete('recipientId');
        navigate({pathname: location.pathname, search: params.toString() ? `?${params}` : ''}, {replace: true});
      });
    return () => { mounted = false; };
  }, [location.pathname, location.search, navigate, token]);

  useEffect(() => {
    if (!token || !activeConversationId) { setMessages([]); setMessageLoadError(null); return; }
    let mounted = true;
    setLoadingMessages(true);
    setMessageLoadError(null);
    setMessages([]);
    messagingApi.getConversationMessages(token, activeConversationId).then((items) => {
      if (mounted) setMessages(items);
    }).catch((error) => mounted && setMessageLoadError(error instanceof Error ? error.message : 'Failed to load messages.'))
      .finally(() => mounted && setLoadingMessages(false));
    return () => { mounted = false; };
  }, [activeConversationId, messageReloadSequence, token]);

  useEffect(() => {
    if (!otherParticipant?.id) { setProfile(null); return; }
    const controller = new AbortController();
    setProfileLoading(true);
    setProfile(null);
    profileApi.getProfile(otherParticipant.id, controller.signal).then(setProfile).catch((error) => {
      if (error?.code !== 'ERR_CANCELED') setProfile(null);
    }).finally(() => { if (!controller.signal.aborted) setProfileLoading(false); });
    return () => controller.abort();
  }, [otherParticipant?.id]);

  useEffect(() => {
    const query = searchQuery.trim();
    if (!token || query.length < 2 || inboxView === 'archived') { setContacts([]); return; }
    let mounted = true;
    const timeout = window.setTimeout(() => {
      setContactsLoading(true);
      messagingApi.searchContacts(token, query).then((items) => mounted && setContacts(items))
        .catch(() => mounted && setContacts([])).finally(() => mounted && setContactsLoading(false));
    }, 250);
    return () => { mounted = false; window.clearTimeout(timeout); };
  }, [inboxView, searchQuery, token]);

  useEffect(() => {
    if (!socket) return;
    const onMessage = ({message}: {message: MessagingMessage}) => {
      if (message.conversationId === activeConversationId) {
        setMessages((current) => mergeMessages(current, [message]));
        if (message.recipient.id === currentUser?.id) setPendingLiveReadId(message.conversationId);
      }
      setArchivedConversations((current) => current.filter((item) => item.id !== message.conversationId));
    };
    const onConversation = ({conversation}: {conversation: MessagingConversation}) => {
      setArchivedConversations((current) => current.filter((item) => item.id !== conversation.id));
      setActiveConversations((current) => mergeConversations(current, [conversation]));
    };
    const onPresence = ({userId, status}: {userId: string; status: MessagingParticipant['presenceStatus']}) => {
      const update = (items: MessagingConversation[]) => items.map((conversation) => ({
        ...conversation,
        participants: conversation.participants.map((participant) => participant.id === userId
          ? {...participant, presenceStatus: status} : participant),
      }));
      setActiveConversations(update);
      setArchivedConversations(update);
    };
    const onRead = ({conversationId, readerId}: {conversationId: string; readerId: string}) => {
      if (conversationId === activeConversationId) {
        setMessages((current) => current.map((message) => message.recipient.id === readerId ? {...message, isRead: true} : message));
      }
    };
    socket.on('messaging:message-created', onMessage);
    socket.on('messaging:conversation-updated', onConversation);
    socket.on('presence:user-status-updated', onPresence);
    socket.on('messaging:messages-read', onRead);
    return () => {
      socket.off('messaging:message-created', onMessage);
      socket.off('messaging:conversation-updated', onConversation);
      socket.off('presence:user-status-updated', onPresence);
      socket.off('messaging:messages-read', onRead);
    };
  }, [activeConversationId, currentUser?.id, socket]);

  useEffect(() => {
    if (reconnectSequence > 0) loadConversations().catch(() => undefined);
  }, [loadConversations, reconnectSequence]);

  useEffect(() => {
    const list = messageListRef.current;
    if (!list || loadingMessages) return;
    const isInitial = previousMessageCountRef.current === 0;
    const nearBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 160;
    if (isInitial || nearBottom || messages[messages.length - 1]?.sender.id === currentUser?.id) {
      if (typeof list.scrollTo === 'function') {
        list.scrollTo({top: list.scrollHeight, behavior: isInitial ? 'auto' : 'smooth'});
      } else {
        list.scrollTop = list.scrollHeight;
      }
    }
    previousMessageCountRef.current = messages.length;
  }, [currentUser?.id, loadingMessages, messages]);

  useEffect(() => { previousMessageCountRef.current = 0; }, [activeConversationId]);

  useEffect(() => {
    if (!moreMenuOpen && !emojiOpen && !profileDrawerOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (moreMenuOpen && !moreMenuRef.current?.contains(target)) setMoreMenuOpen(false);
      if (emojiOpen && !emojiRef.current?.contains(target)) setEmojiOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMoreMenuOpen(false);
        setEmojiOpen(false);
        setProfileDrawerOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [emojiOpen, moreMenuOpen, profileDrawerOpen]);

  useEffect(() => {
    if (profileDrawerOpen) drawerCloseRef.current?.focus();
  }, [profileDrawerOpen]);

  const acknowledgeLiveRead = async () => {
    if (!token || !activeConversationId || pendingLiveReadId !== activeConversationId) return;
    setPendingLiveReadId(null);
    try { await messagingApi.markConversationAsRead(token, activeConversationId); }
    catch (error) {
      setPendingLiveReadId(activeConversationId);
      setSendError(error instanceof Error ? error.message : 'Failed to sync read state.');
    }
  };

  const selectConversation = (conversation: MessagingConversation) => {
    setActiveConversationId(conversation.id);
    setMobileThreadOpen(true);
    setMoreMenuOpen(false);
  };

  const selectInboxView = (view: InboxView) => {
    setInboxView(view);
    const source = view === 'archived' ? archivedConversations : activeConversations;
    const next = view === 'unread' ? source.find((item) => item.unreadCount > 0) : source[0];
    setActiveConversationId(next?.id || null);
    setMobileThreadOpen(false);
    setMoreMenuOpen(false);
  };

  const startConversation = async (participant: MessagingParticipant) => {
    if (!token) return;
    try {
      const conversation = await messagingApi.createConversation(token, participant.id);
      setActiveConversations((current) => mergeConversations(current, [conversation]));
      setActiveConversationId(conversation.id);
      setInboxView('all');
      setSearchQuery('');
      setContacts([]);
      setMobileThreadOpen(true);
    } catch (error) { setPageError(error instanceof Error ? error.message : 'Failed to start conversation.'); }
  };

  const sendMessage = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!token || !currentUser || !activeConversation || (!composerText.trim() && !selectedFiles.length)) return;
    const recipient = getOtherParticipant(activeConversation, currentUser.id);
    if (!recipient) return setSendError('This conversation does not have a valid recipient.');
    setSendLoading(true);
    setSendError(null);
    try {
      const attachments: MessagingAttachmentInput[] = [];
      for (const file of selectedFiles) {
        const session = await socialFeedApi.createMediaUploadSession(token, {
          purpose: 'message-attachment', fileName: file.name, contentType: file.type, fileSize: file.size,
        });
        try {
          if (session.upload.strategy === 'single-put') {
            await socialFeedApi.uploadSinglePutSessionFile(session, file);
            attachments.push({uploadSessionId: session.uploadSession.id});
          } else {
            const [parts, durationSeconds] = await Promise.all([
              socialFeedApi.uploadMultipartSessionFile(token, session, file), getVideoDurationSeconds(file),
            ]);
            attachments.push({uploadSessionId: session.uploadSession.id, parts, durationSeconds});
          }
        } catch (error) {
          await socialFeedApi.abortMediaUploadSession(token, session.uploadSession.id).catch(() => undefined);
          throw error;
        }
      }
      const sent = await messagingApi.sendMessage(token, {
        conversationId: activeConversation.id, recipientId: recipient.id,
        content: composerText.trim(), attachments,
      });
      setMessages((current) => mergeMessages(current, [sent]));
      setActiveConversations((current) => mergeConversations(current, [{
        ...activeConversation, lastMessageAt: sent.createdAt, updatedAt: sent.updatedAt,
        lastMessagePreview: buildLastMessagePreview(sent), unreadCount: 0,
      }]));
      setArchivedConversations((current) => current.filter((item) => item.id !== activeConversation.id));
      setComposerText('');
      setSelectedFiles([]);
      setEmojiOpen(false);
      if (composerRef.current) composerRef.current.style.height = 'auto';
    } catch (error) { setSendError(error instanceof Error ? error.message : 'Failed to send message.'); }
    finally { setSendLoading(false); }
  };

  const headerStyle = profile?.appearance?.homeHeader;
  const profileHeaderStyle = headerStyle?.theme === 'custom' && headerStyle.customImageUrl ? {
    backgroundImage: `linear-gradient(rgba(82,62,172,.2), rgba(82,62,172,.2)), url("${headerStyle.customImageUrl}")`,
    backgroundPosition: `${headerStyle.imagePosition.x}% ${headerStyle.imagePosition.y}%`,
    backgroundSize: `${Math.max(100, headerStyle.imageZoom * 100)}%`,
  } : undefined;

  const renderProfilePanel = (drawer = false) => (
    <aside className={`${styles.profilePanel} ${drawer ? styles.profileDrawer : ''}`.trim()} aria-label="Conversation profile" role={drawer ? 'dialog' : undefined} aria-modal={drawer ? true : undefined}>
      {drawer ? (
        <button ref={drawerCloseRef} className={styles.drawerClose} type="button" onClick={() => setProfileDrawerOpen(false)} aria-label="Close profile details"><X size={20} /></button>
      ) : null}
      {otherParticipant ? (
        <>
          <div className={`${styles.profileCover} ${styles[`cover${headerStyle?.theme || 'purple'}`] || ''}`} style={profileHeaderStyle} />
          <div className={styles.profileIdentity}>
            <ParticipantAvatar participant={profilePanelParticipant || otherParticipant} showPresence className={styles.profileAvatar} />
            <h2>{otherParticipant.name}</h2>
          </div>
          <div className={styles.profileDetails}>
            <h3>About</h3>
            {profileLoading ? <div className={styles.profileSkeleton} aria-label="Loading profile" /> : (
              <>
                <p className={styles.bio}>{profile?.profile.bio || 'Connect, share, and express yourself.'}</p>
                {profile?.createdAt ? <div className={styles.detailRow}><span className={styles.calendarFilledIcon} style={calendarFilledIconStyle} aria-hidden="true" /><span>Member since<strong>{safeDate(profile.createdAt)?.toLocaleDateString([], {month: 'long', day: 'numeric', year: 'numeric'})}</strong></span></div> : null}
                {profile?.profile.location ? <div className={styles.detailRow}><span className={styles.locationLineIcon} style={locationLineIconStyle} aria-hidden="true" /><span>Location<strong>{profile.profile.location}</strong></span></div> : null}
                {otherParticipant.enterprise ? <div className={styles.detailRow}><Globe2 size={18} /><span>Organization<strong>{otherParticipant.enterprise.organizationName}</strong></span></div> : null}
              </>
            )}
          </div>
          <button className={styles.profileButton} type="button" onClick={() => navigate(`/profile?userId=${encodeURIComponent(otherParticipant.id)}`)}>View full profile</button>
        </>
      ) : null}
    </aside>
  );

  const retryLoadConversations = async () => {
    setPageError(null);
    setLoadingConversations(true);

    try {
      await loadConversations();
    } catch (error) {
      setPageError(error instanceof Error ? error.message : 'Failed to load conversations.');
    } finally {
      setLoadingConversations(false);
    }
  };

  return (
    <div className={`${styles.pageRoot} ${mobileThreadOpen ? styles.mobileThreadPage : ''}`.trim()}>
      <div className={styles.pageContent}>
        <div className={styles.pageSectionShell}>
        {!pageError && !loadingConversations ? <header className={styles.pageHeader}>
          <div className={styles.pageHeaderTitleRow}>
            <span
              className={styles.pageHeaderIconTile}
              style={messageFillIconStyle}
              aria-hidden="true"
            >
              <span className={styles.pageHeaderIcon} />
            </span>
            <h1>Messages</h1>
          </div>
          <p>Connect, share and express yourself with others.</p>
        </header> : null}
        {pageError ? (
          <div className={styles.errorStateCard} role="alert">
            <ProfileWarningIcon />
            <h3 className={styles.errorStateTitle}>Unable to load messages</h3>
            <p className={styles.errorStateDescription}>{pageError}</p>
            <button
              type="button"
              className={styles.errorStateRetryButton}
              onClick={() => void retryLoadConversations()}
            >
              Try Again
            </button>
          </div>
        ) : loadingConversations ? (
          <div className={styles.loadingStateCard} role="status">
            <span className={styles.loadingStateSpinner} aria-hidden="true" />
            <h3 className={styles.loadingStateTitle}>Loading messages</h3>
            <p className={styles.loadingStateDescription}>
              We&apos;re loading your conversations and messages from the database.
            </p>
          </div>
        ) : (
          <div className={`${styles.messagesLayout} ${mobileThreadOpen ? styles.mobileThreadActive : ''}`}>
          <aside className={styles.sidebar} aria-label="Conversations">
            <div className={styles.searchBox}>
              <svg className={styles.searchIcon} viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M14.1667 14.1667L18 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="8.75" cy="8.75" r="5.75" stroke="currentColor" strokeWidth="1.8" />
              </svg>
              <input type="search" className={styles.searchInput} value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Search people" aria-label="Search people and conversations" />
            </div>
            <div className={styles.tabs} role="tablist" aria-label="Conversation views">
              {(['all', 'unread'] as InboxView[]).map((view) => (
                <button key={view} role="tab" aria-selected={inboxView === view} className={inboxView === view ? styles.activeTab : ''} type="button" onClick={() => selectInboxView(view)}>{view[0].toUpperCase() + view.slice(1)}</button>
              ))}
            </div>

            <div className={styles.conversationList}>
              {contactsLoading ? <div className={styles.searching}>Searching…</div> : null}
              {contacts.length > 0 && searchQuery.trim().length >= 2 ? (
                <div className={styles.contactSection}>
                  <span>People</span>
                  {contacts.slice(0, 4).map((contact) => (
                    <button key={contact.id} type="button" className={styles.conversationRow} onClick={() => void startConversation(contact)}>
                      <ParticipantAvatar participant={contact} showPresence />
                      <span className={styles.conversationContent}>
                        <span className={styles.conversationTop}><strong>{contact.name}</strong></span>
                        <span className={styles.conversationBottom}><span>Start a conversation</span></span>
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}
              {loadingConversations ? Array.from({length: 4}).map((_, index) => <div key={index} className={styles.conversationSkeleton} />) : null}
              {!loadingConversations && filteredConversations.map((conversation) => {
                const participant = getOtherParticipant(conversation, currentUser?.id);
                if (!participant) return null;
                return (
                  <button key={conversation.id} type="button" className={`${styles.conversationRow} ${activeConversationId === conversation.id ? styles.conversationActive : ''}`.trim()} onClick={() => selectConversation(conversation)}>
                    <ParticipantAvatar participant={participant} showPresence />
                    <span className={styles.conversationContent}>
                      <span className={styles.conversationTop}><strong>{participant.name}</strong><time>{formatConversationDate(conversation.lastMessageAt)}</time></span>
                      <span className={styles.conversationBottom}><span>{conversation.lastMessagePreview || 'No messages yet'}</span>{conversation.unreadCount ? <b>{conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}</b> : null}</span>
                    </span>
                  </button>
                );
              })}
              {!loadingConversations && !filteredConversations.length ? <div className={styles.emptyList}><span className={styles.emptyListIcon} style={messageOutlineIconStyle} aria-hidden="true" /><strong>{inboxView === 'archived' ? 'No archived conversations' : inboxView === 'unread' ? 'You’re all caught up' : 'No conversations found'}</strong><span>{searchQuery ? 'Try a different search.' : 'New conversations will appear here.'}</span></div> : null}
            </div>
          </aside>

          <section className={styles.threadPanel} onClickCapture={() => void acknowledgeLiveRead()} onFocusCapture={() => void acknowledgeLiveRead()}>
            {activeConversation && otherParticipant ? (
              <>
                <header className={styles.threadHeader}>
                  <button className={styles.mobileBack} type="button" onClick={() => setMobileThreadOpen(false)} aria-label="Back to conversations"><span className={styles.mobileBackIcon} style={arrowLeftBoldIconStyle} aria-hidden="true" /></button>
                  <button className={styles.threadPerson} type="button" onClick={() => setProfileDrawerOpen(true)}>
                    <ParticipantAvatar participant={otherParticipant} showPresence />
                    <span>
                      <strong>{otherParticipant.name}</strong>
                    </span>
                  </button>
                  <div className={styles.threadActions}>
                    <button type="button" disabled title="Audio calling coming soon" aria-label="Audio calling coming soon"><Phone size={19} /></button>
                    <button type="button" disabled title="Video calling coming soon" aria-label="Video calling coming soon"><Video size={20} /></button>
                    <div className={styles.moreWrap} ref={moreMenuRef}>
                      <button type="button" aria-label="Conversation options" aria-haspopup="menu" aria-expanded={moreMenuOpen} onClick={() => setMoreMenuOpen((value) => !value)}><span className={styles.conversationOptionsIcon} style={conversationOptionsIconStyle} aria-hidden="true" /></button>
                      {moreMenuOpen ? <div className={styles.moreMenu} role="menu">
                        <button role="menuitem" type="button" onClick={() => navigate(`/profile?userId=${encodeURIComponent(otherParticipant.id)}`)}>View profile</button>
                      </div> : null}
                    </div>
                  </div>
                </header>

                <div className={styles.messageList} ref={messageListRef} aria-live="polite">
                  {messageLoadError ? <div className={styles.threadErrorState} role="alert"><ProfileWarningIcon /><h3 className={styles.errorStateTitle}>Unable to load messages</h3><p className={styles.errorStateDescription}>{messageLoadError}</p><button type="button" className={styles.errorStateRetryButton} onClick={() => setMessageReloadSequence((value) => value + 1)}>Try Again</button></div> : null}
                  {!messageLoadError && loadingMessages ? <div className={styles.threadLoading}><span /><span /><span /></div> : null}
                  {!messageLoadError && !loadingMessages && !messages.length ? <div className={styles.emptyThread}><span className={styles.emptyThreadIcon} style={messageFillIconStyle} aria-hidden="true" /><h2>Start the conversation</h2><p>Send a message or share a photo or video.</p></div> : null}
                  {!messageLoadError && !loadingMessages && messages.map((message, index) => {
                    const mine = message.sender.id === currentUser?.id;
                    const previous = messages[index - 1];
                    const showDay = !previous || safeDate(previous.createdAt)?.toDateString() !== safeDate(message.createdAt)?.toDateString();
                    return <React.Fragment key={message.id}>
                      {showDay ? <div className={styles.daySeparator}><span>{formatDay(message.createdAt)}</span></div> : null}
                      <div className={`${styles.messageRow} ${mine ? styles.mine : ''}`.trim()}>
                        {!mine ? <ParticipantAvatar participant={message.sender} className={styles.messageAvatar} /> : null}
                        <div className={`${styles.messageBubble} ${mine ? styles.mineBubble : ''}`.trim()}>
                          {message.content ? <p>{message.content}</p> : null}
                          {message.attachments.length ? <div className={styles.attachments}>{message.attachments.map((attachment, attachmentIndex) => attachment.type === 'image'
                            ? <a href={attachment.url} target="_blank" rel="noreferrer" key={`${attachment.url}-${attachmentIndex}`}><img src={attachment.url} alt="Message attachment" /></a>
                            : <video key={`${attachment.url}-${attachmentIndex}`} src={attachment.url} controls preload="metadata" />)}</div> : null}
                          <span className={styles.messageMeta}><time>{formatTime(message.createdAt)}</time>{mine ? <CheckCheck size={14} className={message.isRead ? styles.read : ''} aria-label={message.isRead ? 'Read' : 'Sent'} /> : null}</span>
                        </div>
                      </div>
                    </React.Fragment>;
                  })}
                </div>

                <div className={styles.composerArea}>
                  {sendError ? <div className={styles.sendError} role="alert"><span>{sendError}</span><button type="button" onClick={() => void sendMessage()}>Retry</button></div> : null}
                  {selectedFiles.length ? <div className={styles.filePreviews}>{selectedFiles.map((file) => <span key={`${file.name}-${file.lastModified}`}><i className={styles.filePreviewPaperclipIcon} style={paperclipIconStyle} aria-hidden="true" />{file.name}<button type="button" aria-label={`Remove ${file.name}`} onClick={() => setSelectedFiles((items) => items.filter((item) => item !== file))}><X size={13} /></button></span>)}</div> : null}
                  <form className={styles.composer} onSubmit={sendMessage}>
                    <button type="button" className={styles.attachControl} aria-label="Attach image or video" onClick={() => fileInputRef.current?.click()}><span className={styles.paperclipIcon} style={paperclipIconStyle} aria-hidden="true" /></button>
                    <input ref={fileInputRef} type="file" hidden multiple accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/x-msvideo" onChange={(event) => { setSelectedFiles((items) => [...items, ...Array.from(event.target.files || [])]); event.target.value = ''; }} />
                    <textarea className={styles.composerInput} ref={composerRef} rows={1} maxLength={5000} value={composerText} placeholder="Type a message…" aria-label="Message" onChange={(event) => { setComposerText(event.target.value); event.currentTarget.style.height = 'auto'; event.currentTarget.style.height = `${Math.min(event.currentTarget.scrollHeight, 110)}px`; }} onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); void sendMessage(); }
                    }} />
                    <div className={styles.emojiWrap} ref={emojiRef}>
                      <button type="button" className={styles.emojiControl} aria-label="Choose emoji" aria-expanded={emojiOpen} onClick={() => setEmojiOpen((value) => !value)}><Smile size={22} /></button>
                      {emojiOpen ? <div className={styles.emojiPicker} role="menu" aria-label="Emoji picker">{EMOJIS.map((emoji) => <button key={emoji} role="menuitem" type="button" onClick={() => { setComposerText((text) => `${text}${emoji}`); setEmojiOpen(false); composerRef.current?.focus(); }}>{emoji}</button>)}</div> : null}
                    </div>
                    <button className={styles.sendButton} type="submit" disabled={sendLoading || (!composerText.trim() && !selectedFiles.length)}>{sendLoading ? <span className={styles.spinner} aria-label="Sending" /> : <><span>Send</span><i className={styles.sendButtonIcon} style={sendIconStyle} aria-hidden="true" /></>}</button>
                  </form>
                  <span className={styles.characterCount}>{composerText.length > 4500 ? `${composerText.length}/5000` : ''}</span>
                </div>
              </>
            ) : <div className={styles.emptyThread}><span className={`${styles.emptyThreadIcon} ${styles.emptyThreadIconLarge}`} style={messageFillIconStyle} aria-hidden="true" /><h2>Select a conversation</h2><p>Choose a conversation to start messaging.</p></div>}
          </section>

          {renderProfilePanel()}
          </div>
        )}
        </div>
      </div>
      {profileDrawerOpen ? <div className={styles.drawerBackdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) setProfileDrawerOpen(false); }}>{renderProfilePanel(true)}</div> : null}
    </div>
  );
};

export default Messages;
